import React from 'react';
import classNames from 'classnames';
import dynamic from 'next/dynamic';
import * as PropTypes from 'prop-types';

import { withTracking } from 'hooks';
import {
  buildCitySubpageUrl,
  buildCMPPageUrl,
  buildCmpSubpagesUrls,
  buildCompanyCitySalaryUrl,
  buildCompanyJobsCityUrl,
  buildCompanyJobsJobTitleUrl,
  buildCompanyJobTitleSalaryUrl,
  buildCompanyStateSalaryUrl,
  buildCppSubpageUrl,
  buildFullLocation,
  buildMajorPageUrl,
  buildSitemapLocationsPageUrl,
  buildUrlUsingTitleAndLocation,
} from 'utils';
import {
  CITY_SUBPAGES,
  CLUSTER_HEADER_CATEGORY,
  CMP_SUBPAGES,
  CPP_SUBPAGES,
  MAJOR_SUBPAGES,
  TRACKING,
} from 'utils/constant';
import { SEARCH_SOURCES } from 'utils/constants-v2';
import { gtm } from 'utils/gtm';

import classes from './searchBar.module.css';

const AutosuggestInput = dynamic(() => import('components/autosuggestInput'), { ssr: false });

const propTypes = {
  subPageName: PropTypes.string.isRequired,
  clusterCategory: PropTypes.string.isRequired,
  jobLocation: PropTypes.string,
  jobTitleArticle: PropTypes.string,
  className: PropTypes.string,
  criteria: PropTypes.object,
};

const defaultProps = {
  jobLocation: '',
  jobTitleArticle: '',
  className: '',
};

// dataset for handling index/noindex pages for CMP, MAJOR, CPP subpages.
const SUGGESTIONS_FILTER = {
  // CMP_SUBPAGES display flags INDEX/NOINDEX
  CAREER: {
    [CMP_SUBPAGES.JOBS]: 'indexInfoOfJobsPage',
    [CMP_SUBPAGES.SALARY]: 'indexInfoOfSalaryPage',
    [CMP_SUBPAGES.RESUME]: 'indexInfoOfResumePage',
    [CMP_SUBPAGES.SKILLS]: 'indexInfoOfSkillsPage',
    [CMP_SUBPAGES.WHAT_THEY_DO]: 'indexInfoOfWhatTheyDoPage',
    [CMP_SUBPAGES.HOW_TO_BECOME]: 'indexInfoOfHowToBecomePage',
    [CMP_SUBPAGES.EDUCATION]: 'indexInfoOfEducationPage',
    [CMP_SUBPAGES.CAREER_PATH]: 'indexInfoOfCareerPathPage',
    [CMP_SUBPAGES.BEST_STATES]: 'indexInfoOfBestStatesPage',
    [CMP_SUBPAGES.BEST_CITIES]: 'indexInfoOfBestCitiesPage',
    [CMP_SUBPAGES.REMOTE_JOBS]: 'indexInfoOfRemoteJobsPage',
    [CMP_SUBPAGES.TRENDS]: 'indexInfoOfTrendsPage',
    [CMP_SUBPAGES.DEMOGRAPHICS]: 'indexInfoOfDemographicsPage',
    [CMP_SUBPAGES.CERTIFICATIONS]: 'indexInfoOfCertificationsPage',
    [CMP_SUBPAGES.DESCRIPTION]: 'indexInfoOfDescriptionPage',
    [CMP_SUBPAGES.FAQ]: 'indexInfoOfFAQPage',
    [CMP_SUBPAGES.COMPARE]: 'indexInfoOfCareerComparePage',
    [CMP_SUBPAGES.HOW_TO_HIRE]: 'indexInfoOfHowToHirePage',
    [CMP_SUBPAGES.SITEMAP_LOCATION]: 'indexInfoOfLocationsPage',
    [CMP_SUBPAGES.BEST_COMPANIES]: 'indexInfoOfBestCompaniesPage',
  },
  // MAJOR_SUBPAGES diplsay flags true/false
  MAJOR: {
    [MAJOR_SUBPAGES.SALARY]: 'displayFlagOfMajorSalaryPage',
    [MAJOR_SUBPAGES.BEST_COLLEGES]: 'displayFlagOfBestCollegesPage',
  },
  // CPP_SUBPAGES display flags  true/false
  COMPANY: {
    [CPP_SUBPAGES.JOBS]: 'displayFlagOfCompanyJobsPage',
    [CPP_SUBPAGES.SALARY]: 'displayFlagOfCSP',
    [CPP_SUBPAGES.HISTORY]: 'displayFlagOfCompanyHistoryPage',
    [CPP_SUBPAGES.DEMOGRAPHICS]: 'displayFlagOfCompanyDemographicsPage',
    [CPP_SUBPAGES.COMPETITORS]: 'displayFlagOfCompanyCompetitorsPage',
    [CPP_SUBPAGES.EXECUTIVES]: 'displayFlagOfCompanyExecutivesPage',
    [CPP_SUBPAGES.FAQ]: 'displayFlagOfCompanyFAQPage',
    [CPP_SUBPAGES.MISSION_STATEMENT]: 'displayFlagOfCompanyMissionStatementPage',
    [CPP_SUBPAGES.REVIEWS]: 'displayFlagOfCompanyReviewsPage',
    [CPP_SUBPAGES.SUBSIDIARIES]: 'displayFlagOfCompanySubsidiariesPage',
    [CPP_SUBPAGES.REVENUE]: 'displayFlagOfCompanyRevenuePage',
  },
  // CITY_SUBPAGES display flags  true/false
  CITY: {
    [CITY_SUBPAGES.SALARY]: 'displayFlagForSalaryPage',
    [CITY_SUBPAGES.PART_TIME_JOBS]: 'displayFlagForPartTimeJobsPage',
  },
};

class SearchBar extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      error: '',
    };
  }

  updateTitleSearch = (input) => {
    const { error } = this.state;
    const { jobLocation, dynamicPageName, tracking } = this.props;
    if (error && input.isListOpen) {
      this.setState({
        error: '',
      });
    }
    if (input.value) {
      const { original_name, article } = input.valueObj;
      const jobTitlePlural = input.valueObj.title_indexinfo.jobTitlePlural || '';

      const subPageName = this.handleNoIndexPages(input.valueObj);

      let url = '';
      if (subPageName === CMP_SUBPAGES.JOBS && jobLocation) {
        url += buildUrlUsingTitleAndLocation(original_name, jobLocation, '');
      } else if (subPageName === CMP_SUBPAGES.DYNAMIC && dynamicPageName) {
        url += buildCmpSubpagesUrls(original_name, '', subPageName, dynamicPageName);
      } else if (subPageName === CMP_SUBPAGES.OVERVIEW || !subPageName) {
        url += buildCMPPageUrl(original_name);
      } else if (subPageName === CMP_SUBPAGES.SITEMAP_LOCATION) {
        url += buildSitemapLocationsPageUrl(original_name);
      } else {
        url += buildCmpSubpagesUrls(
          original_name,
          '',
          subPageName,
          '',
          article || '',
          '',
          jobTitlePlural
        );
      }

      tracking.logActivity(
        {
          title: {
            originalSearch: input.value,
            normalizedTitle: input.value,
          },
        },
        TRACKING.LOG_ACTIVITY.AUTO_COMPLETE
      );

      gtm.logSearch(input.originalValue, input.value, SEARCH_SOURCES.CLUSTER_HEADER);

      window.location.href = url;
    }
  };

  updateCompanySearch = (input) => {
    const { error } = this.state;
    const { subPageName, dynamicPage, criteria } = this.props;
    if (error && input.isListOpen) {
      this.setState({
        error: '',
      });
    }
    if (input.valueObj.companyID) {
      let subPage = this.handleNoIndexPages(input.valueObj);
      if (dynamicPage) {
        subPage = subPage.split(' ').join('-').toLowerCase();
      }
      let url;
      if (subPageName === CPP_SUBPAGES.LOCATION_COMBO_JOBS && criteria) {
        url = buildCompanyJobsCityUrl(
          input.valueObj.zippiaCppUrl,
          criteria.city,
          criteria.stateAbbr
        );
      } else if (subPageName === CPP_SUBPAGES.JOB_TITLE_COMBO_JOBS && criteria) {
        url = buildCompanyJobsJobTitleUrl(input.valueObj.zippiaCppUrl, criteria.title);
      } else if (subPageName === CPP_SUBPAGES.JOB_TITLE_SALARY) {
        url = buildCompanyJobTitleSalaryUrl(input.valueObj.zippiaCppUrl, criteria.title);
      } else if (subPageName === CPP_SUBPAGES.STATE_SALARY) {
        url = buildCompanyStateSalaryUrl(input.valueObj.zippiaCppUrl, criteria.state);
      } else if (subPageName === CPP_SUBPAGES.CITY_SALARY) {
        url = buildCompanyCitySalaryUrl(
          input.valueObj.zippiaCppUrl,
          criteria.city,
          criteria.stateAbbr
        );
      } else {
        url = buildCppSubpageUrl(input.valueObj.zippiaCppUrl, '', subPage);
        if (subPageName === CPP_SUBPAGES.JOBS) {
          localStorage.removeItem('cmpQuery');
        }
      }
      gtm.logSearch(input.originalValue, input.valueObj.companyName, SEARCH_SOURCES.CLUSTER_HEADER);
      window.location.href = url;
    }
  };

  updateMajorSearch = (input) => {
    const { error } = this.state;
    const { dynamicPage, subPageName } = this.props;
    if (error && input.isListOpen) {
      this.setState({
        error: '',
      });
    }

    if (input.value) {
      const majorName = input.valueObj ? input.valueObj.original_name : '';
      const originalUrl = buildMajorPageUrl(majorName).toLowerCase();

      let subPageRoute = this.handleNoIndexPages(input.valueObj);

      if (dynamicPage) {
        subPageRoute = subPageName.split(' ').join('-').toLowerCase();
      }

      const url = subPageRoute ? `${originalUrl}${subPageRoute}/` : originalUrl;

      gtm.logSearch(
        input.originalValue,
        input?.valueObj?.original_name || '',
        SEARCH_SOURCES.CLUSTER_HEADER
      );

      window.location.href = url;
    }
  };

  updateCitySearch = (input) => {
    const { error } = this.state;
    if (error && input.isListOpen) {
      this.setState({
        error: '',
      });
    }

    if (input.valueObj && Object.keys(input.valueObj).length > 0) {
      const { city, state } = input.valueObj;

      const subPageRoute = this.handleNoIndexPages(input.valueObj);

      const location = buildFullLocation(city, null, state);

      gtm.logSearch(input.originalValue, location, SEARCH_SOURCES.CLUSTER_HEADER);

      const url = buildCitySubpageUrl({ city, stateAbbr: state }, subPageRoute);
      window.location.href = url;
    }
  };

  /**
   * @param valueObj -> value object from autosuggestion input
   * @returns {subPageName} -> current subPageName or empty string which means Overview page;
   */
  handleNoIndexPages = (valueObj) => {
    const { clusterCategory, subPageName } = this.props;

    switch (clusterCategory) {
      case CLUSTER_HEADER_CATEGORY.CAREER:
        return SUGGESTIONS_FILTER.CAREER[subPageName] &&
          valueObj.title_indexinfo &&
          valueObj.title_indexinfo[SUGGESTIONS_FILTER.CAREER[subPageName]] === 'NOINDEX'
          ? ''
          : subPageName;
      case CLUSTER_HEADER_CATEGORY.MAJOR:
        return SUGGESTIONS_FILTER.MAJOR[subPageName] &&
          valueObj.major_indexinfo &&
          valueObj.major_indexinfo[SUGGESTIONS_FILTER.MAJOR[subPageName]]
          ? subPageName
          : '';
      case CLUSTER_HEADER_CATEGORY.COMPANY:
        return SUGGESTIONS_FILTER.COMPANY[subPageName] &&
          !valueObj[SUGGESTIONS_FILTER.COMPANY[subPageName]]
          ? 'overview'
          : subPageName;
      case CLUSTER_HEADER_CATEGORY.CITY:
        return SUGGESTIONS_FILTER.CITY[subPageName] &&
          valueObj?.[SUGGESTIONS_FILTER.CITY[subPageName]]
          ? subPageName
          : '';
    }
  };

  handleAutosuggestError = (errorObject) => {
    this.setState({
      error: errorObject.errorMessage,
    });
  };

  render() {
    const { error } = this.state;
    const { clusterCategory, className } = this.props;

    return (
      <div
        className={classNames('px-0', classes['cluster-header-search-bar-container'], className, {
          'containsError z-mb-0 z-pb-md-54': error,
        })}
      >
        {clusterCategory === CLUSTER_HEADER_CATEGORY.CAREER && (
          <AutosuggestInput
            category="jobTitle"
            inputProps={{
              className: 'typeahead form-control filterInput',
              name: 'jobTitle',
              placeholder: 'Search For A Job Title',
            }}
            onChange={this.updateTitleSearch}
            onErrorCallback={this.handleAutosuggestError}
            isIndexableOnly
          />
        )}

        {clusterCategory === CLUSTER_HEADER_CATEGORY.COMPANY && (
          <AutosuggestInput
            category="company"
            inputProps={{
              className: 'typeahead form-control filterInput',
              name: 'company',
              placeholder: 'Search For A Company',
            }}
            onChange={this.updateCompanySearch}
            onErrorCallback={this.handleAutosuggestError}
            isIndexableOnly
          />
        )}

        {clusterCategory === CLUSTER_HEADER_CATEGORY.MAJOR && (
          <AutosuggestInput
            category="major"
            inputProps={{
              className: 'typeahead form-control filterInput',
              name: 'major',
              placeholder: 'Search For A Major',
            }}
            onChange={this.updateMajorSearch}
            onErrorCallback={this.handleAutosuggestError}
            isIndexableOnly
          />
        )}

        {clusterCategory === CLUSTER_HEADER_CATEGORY.CITY && (
          <AutosuggestInput
            category="location"
            inputProps={{
              name: 'city',
              className: 'typeahead form-control filterInput',
              placeholder: 'Search For A City',
              id: 'cityClusterSearchField',
            }}
            onChange={this.updateCitySearch}
            onErrorCallback={this.handleAutosuggestError}
            cityOnlyLocations={true}
          />
        )}

        {error && <p className={classes['error-message']}>{error}</p>}
      </div>
    );
  }
}

SearchBar.propTypes = propTypes;
SearchBar.defaultProps = defaultProps;

export default withTracking(SearchBar);
